import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icon';
import { SearchInput } from '@/components/SearchInput';
import { TableHeaderActions } from '@/components/TableHeaderActions';
import { Modal } from '@/components/Modal';
import { Badge } from '@/components/Badge';
import { Tooltip } from '@/components/Tooltip';
import { date, price, percentage } from '@/utils/format';
import { RowActions } from '@/components/RowActions';
import { ListPagination, type ListPaginationProps } from '@/components/ListPagination';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { ListToolbar } from '@/components/ListToolbar';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

export type TableColumnType =
    | 'text'
    | 'multivalue'
    | 'date'
    | 'datetime'
    | 'time'
    | 'boolean'
    | 'price'
    | 'percentage'
    | 'number'
    | 'select';

export interface TableColumn<T = Record<string, unknown>> {
    key: string;
    label: string;
    sortable?: boolean;
    sortKey?: string;
    searchKey?: string;
    align?: 'left' | 'center' | 'right';
    nowrap?: boolean;
    className?: string;
    render?: (row: T) => React.ReactNode;
    type?: TableColumnType;
    max?: number;
    multiValueExtractor?: (row: T) => React.ReactNode[];
    filterOptions?: { value: string | number; label: string }[];
}

export interface TableAction<T = Record<string, unknown>> {
    label: string;
    icon?: React.ReactNode;
    onClick?: (row: T) => void;
    variant?: 'default' | 'delete' | 'primary';
    show?: (row: T) => boolean;
}

export interface TableProps<T = Record<string, unknown>> {
    appearance?: 'default' | 'workspace';
    density?: 'comfortable' | 'compact';
    sort?: { key: string; order: 'asc' | 'desc' };
    onSortChange?: (sort: { key: string; order: 'asc' | 'desc' }) => void;
    manualSorting?: boolean;
    title?: string;
    columns: TableColumn<T>[];
    data: T[];
    actions?: TableAction<T>[];
    actionsPosition?: 'left' | 'right';
    actionMenu?: boolean;
    actionsLabel?: string;
    searchable?: boolean;
    search?: boolean;
    views?: boolean;
    config?: boolean;
    searchPlaceholder?: string;
    searchKeys?: string[];
    searchValue?: string;
    onSearchChange?: (val: string) => void;
    filterElement?: React.ReactNode;
    filterOpen?: boolean;
    onFilterToggle?: () => void;
    filterLabel?: React.ReactNode;
    filters?: React.ReactNode;
    filterFooter?: React.ReactNode;
    filterPanel?: React.ReactNode;
    paginated?: boolean;
    pagination?: ListPaginationProps;
    manualPagination?: boolean;
    resetPageKey?: string;
    error?: string | null;
    onRetry?: () => void;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    defaultSortKey?: string | null;
    defaultSortOrder?: 'asc' | 'desc';
    onRowClick?: (row: T) => void;
    emptyText?: string;
    isLoading?: boolean;
    loading?: boolean;
    headerRight?: React.ReactNode;
    className?: string;
    filterbox?: boolean;
    rowClassName?: (row: T) => string;
    fullscreen?: boolean;
    exportable?: boolean;
}

export function TableSimple<T extends object = Record<string, unknown>>({
    appearance = 'default',
    density = 'compact',
    sort,
    onSortChange,
    manualSorting = false,
    title,
    columns = [],
    data = [],
    actions = [],
    actionsPosition = 'right',
    actionMenu = true,
    actionsLabel,
    searchable = true,
    search: showSearch = true,
    searchPlaceholder,
    searchKeys = [],
    searchValue,
    onSearchChange,
    filterElement,
    filterOpen,
    onFilterToggle,
    filterLabel,
    filters,
    filterFooter,
    filterPanel,
    paginated = true,
    pagination,
    manualPagination = Boolean(pagination),
    resetPageKey,
    error,
    onRetry,
    defaultPageSize = 20,
    pageSizeOptions = [10, 20, 50, 100, -1],
    defaultSortKey = null,
    defaultSortOrder = 'asc',
    onRowClick,
    emptyText,
    isLoading = false,
    loading,
    headerRight,
    className = '',
    filterbox = false,
    rowClassName,
    fullscreen = true,
    exportable = false,
}: TableProps<T>): React.ReactElement {
    const isTableLoading = loading ?? isLoading;
    const { t } = useTranslation('global');
    const { showSuccess } = useToast();
    const { user } = useAuth() as { user?: { id?: number | string } | null };

    const resolvedActionsLabel = actionsLabel || (actionMenu ? <span className="sr-only">{t('table.actions')}</span> : t('table.actions'));
    const resolvedSearchPlaceholder = searchPlaceholder || t('table.search_placeholder');
    const resolvedEmptyText = emptyText || t('table.empty');

    const [search, setSearch] = useState<string>('');
    const [internalSortKey, setSortKey] = useState<string | null>(defaultSortKey);
    const [internalSortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);
    const sortKey = sort?.key ?? internalSortKey;
    const sortOrder = sort?.order ?? internalSortOrder;
    const workspace = appearance === 'workspace';
    const [internalPage, setInternalPage] = useState<number>(1);
    const pageSizePreferenceKey = `vigepra_table_page_size:${user?.id ?? 'guest'}:${window.location.pathname}${window.location.search}`;
    const [internalPageSize, setPageSize] = useState<number>(() => {
        try {
            const stored = Number.parseInt(localStorage.getItem(pageSizePreferenceKey) || '', 10);
            return pageSizeOptions.includes(stored) ? stored : defaultPageSize;
        }
        catch {
            return defaultPageSize;
        }
    });
    const requestedPage = pagination?.page ?? internalPage;
    const pageSize = pagination?.pageSize ?? internalPageSize;
    const setCurrentPage = pagination?.onPage ?? setInternalPage;
    useEffect(() => {
        try {
            localStorage.setItem(pageSizePreferenceKey, String(pageSize));
        }
        catch {
            // Local storage is optional.
        }
    }, [pageSize, pageSizePreferenceKey]);
    useEffect(() => { if(resetPageKey !== undefined){ setCurrentPage(1); } }, [resetPageKey]);
    const [multiValueModal, setMultiValueModal] = useState<{ isOpen: boolean, title: string, items: React.ReactNode[] }>({ isOpen: false, title: '', items: [] });
    const [multiValueSearch, setMultiValueSearch] = useState<string>('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportingFormat, setExportingFormat] = useState<'pdf' | 'xlsx' | 'ods' | 'csv' | null>(null);

    useEffect(() => {
        if(!isFullscreen){
            return;
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if(event.key === 'Escape'){
                setIsFullscreen(false);
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullscreen]);

    const isControlledSearch = typeof onSearchChange === 'function';
    const effectiveSearch = isControlledSearch ? (searchValue ?? '') : search;

    const handleSearchChange = (val: string): void => {
        if(isControlledSearch){
            onSearchChange(val);
        }
        else {
            setSearch(val);
        }
        setCurrentPage(1);
    };

    // Filter data by search
    const filteredData = useMemo(() => {
        if(isControlledSearch){
            return data;
        }

        if(!effectiveSearch.trim()){
            return data;
        }

        const q = effectiveSearch.toLowerCase().trim();

        const getValue = (obj: any, path: string) => {
            return path.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
        };

        return data.filter((row) => {
            if(searchKeys.length > 0){
                return searchKeys.some((k) => {
                    const val = getValue(row, k);
                    return String(val ?? '').toLowerCase().includes(q);
                });
            }
            return columns.some((col) => {
                if(col.type === 'multivalue' && col.multiValueExtractor){
                    const items = col.multiValueExtractor(row);
                    if(items && items.some(it => {
                        const text = typeof it === 'string' || typeof it === 'number'
                            ? String(it)
                            : (React.isValidElement(it) && it.props && typeof (it.props as Record<string, unknown>).value === 'string'
                                ? String((it.props as Record<string, unknown>).value)
                                : '');
                        return text.toLowerCase().includes(q);
                    })){
                        return true;
                    }
                }
                const val = getValue(row, col.key as string);
                // If it's an object, we stringify it to search its values (basic fallback)
                if(val !== null && typeof val === 'object'){
                    return JSON.stringify(val).toLowerCase().includes(q);
                }
                return String(val ?? '').toLowerCase().includes(q);
            });
        });
    }, [data, effectiveSearch, searchKeys, columns, isControlledSearch]);

    // Sort data
    const sortedData = useMemo(() => {
        if(!sortKey || manualSorting){
            return filteredData;
        }

        const getValue = (obj: any, path: string) => {
            return path.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
        };

        return [...filteredData].sort((a, b) => {
            const valA = getValue(a, sortKey);
            const valB = getValue(b, sortKey);

            if(typeof valA === 'number' && typeof valB === 'number'){
                return sortOrder === 'asc' ? valA - valB : valB - valA;
            }

            const strA = String(valA ?? '').toLowerCase();
            const strB = String(valB ?? '').toLowerCase();

            if(strA < strB){
                return sortOrder === 'asc' ? -1 : 1;
            }
            if(strA > strB){
                return sortOrder === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortKey, sortOrder, manualSorting]);

    // Pagination
    const totalRecords = pagination?.total ?? sortedData.length;
    const isAll = pageSize === -1;
    const totalPages = isAll ? 1 : (Math.ceil(totalRecords / pageSize) || 1);
    const currentPage = Math.min(requestedPage, totalPages);
    useEffect(() => { if(!manualPagination && requestedPage > totalPages){ setCurrentPage(totalPages); } }, [requestedPage, totalPages, manualPagination]);
    const paginatedData = useMemo(() => {
        if(manualPagination || !paginated || isAll){
            return sortedData;
        }
        const start = (currentPage - 1) * pageSize;
        return sortedData.slice(start, start + pageSize);
    }, [sortedData, paginated, currentPage, pageSize, isAll, manualPagination]);

    const handleSort = (key: string) => {
        if(onSortChange){
            onSortChange({ key, order: sortKey === key && sortOrder === 'asc' ? 'desc' : 'asc' });
            return;
        }
        if(sortKey === key){
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };

    const nodeToText = (node: unknown): string => {
        if(node === null || node === undefined || typeof node === 'boolean'){
            return '';
        }
        if(typeof node === 'string' || typeof node === 'number'){
            return String(node);
        }
        if(Array.isArray(node)){
            return node.map(nodeToText).join('');
        }
        if(React.isValidElement(node)){
            const props = node.props as {
                children?: unknown;
                value?: string | number | Array<string | number>;
                options?: Array<{ value: string | number; label: string }>;
            };

            if(Array.isArray(props.value) && Array.isArray(props.options)){
                return props.value.map((value) => (
                    props.options?.find((option) => String(option.value) === String(value))?.label ?? String(value)
                )).join(', ');
            }

            return props.children !== undefined ? nodeToText(props.children) : String(props.value ?? '');
        }
        return '';
    };

    const copyRow = async (row: T): Promise<void> => {
        const content = columns.map((column) => {
            if(column.multiValueExtractor){
                const extractedValues = column.multiValueExtractor(row);
                const rawValues = Reflect.get(row, column.key);
                const values = extractedValues.length > 0
                    ? extractedValues
                    : Array.isArray(rawValues)
                        ? rawValues.map((value) => {
                            if(value && typeof value === 'object'){
                                const item = value as { name?: string; label?: string; tag?: string };
                                return item.name || item.label || item.tag || '';
                            }
                            return value as React.ReactNode;
                        })
                        : [];
                return `[${column.label}] ${values.map((value) => nodeToText(value)).filter(Boolean).join(', ')}`;
            }

            const rendered = column.render ? column.render(row) : Reflect.get(row, column.key);
            return `[${column.label}] ${nodeToText(rendered)}`;
        }).join(' | ');

        await navigator.clipboard.writeText(content);
        showSuccess(t('common.copied'));
    };

    const copyAction: TableAction<T> = {
        label: t('common.copy'),
        icon: <Icon value="copy" size={14} />,
        onClick: (row: T): void => {
            void copyRow(row).catch((error) => {
                console.error('Table row copy failed', error);
            });
        },
    };
    const editOrDeleteActionIndex = actions.findIndex((action) => (
        action.label === t('table.edit') || action.variant === 'delete'
    ));
    const rowActions: TableAction<T>[] = editOrDeleteActionIndex < 0
        ? [...actions, copyAction]
        : [...actions.slice(0, editOrDeleteActionIndex), copyAction, ...actions.slice(editOrDeleteActionIndex)];

    const renderActionsContent = (row: T) => <RowActions row={row} actions={rowActions} position={actionsPosition} menu={actionMenu} />;
    const exportRows = sortedData.map((row) => columns.reduce<Record<string, string | number>>((result, column) => {
        const value = row[column.key as keyof T];
        result[column.label] = value === null || value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
        return result;
    }, {}));

    const exportTable = (format: 'pdf' | 'xlsx' | 'ods' | 'csv'): void => {
        setExportingFormat(format);
        const filename = (title || 'tabla').toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'tabla';
        const headers = columns.map((column) => column.label);
        const rows = exportRows.map((row) => headers.map((header) => row[header] ?? ''));

        window.setTimeout(() => {
            try {
                if (format === 'pdf') {
                    const pdf = new jsPDF({ orientation: 'landscape' });
                    autoTable(pdf, { head: [headers], body: rows });
                    pdf.save(`${filename}.pdf`);
                } else {
                    const worksheet = XLSX.utils.json_to_sheet(exportRows);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
                    XLSX.writeFile(workbook, `${filename}.${format}`, { bookType: format });
                }

                setExportModalOpen(false);
            } catch (error) {
                console.error('Table export failed', error);
            } finally {
                setExportingFormat(null);
            }
        }, 0);
    };

    const tableActions = [
        fullscreen ? {
            id: 'fullscreen',
            label: isFullscreen ? t('table.fullscreen.exit') : t('table.fullscreen.enter'),
            icon: isFullscreen ? 'minimize' : 'maximize',
            onClick: () => setIsFullscreen((value) => !value),
        } : null,
        exportable ? {
            id: 'export',
            label: t('table.export.action'),
            icon: 'download',
            onClick: () => {
                setExportModalOpen(true);
            },
        } : null,
    ].filter((action): action is {
        id: string;
        label: string;
        icon: string;
        onClick: () => void;
    } => action !== null);

    const toolbarTrailing = headerRight;
    return (
        <div className={`space-y-3 ${workspace ? `[&_.table-simple-tr-head]:!bg-slate-100 [&_.table-simple-tr-head]:!text-slate-600 [&_.table-simple-tr-head]:!normal-case [&_.table-simple-tr-head]:!tracking-normal dark:[&_.table-simple-tr-head]:!bg-zinc-800 dark:[&_.table-simple-tr-head]:!text-zinc-300 [&_.table-simple-th]:!py-3 [&_.table-simple-th]:!text-xs [&_.table-simple-td]:!leading-relaxed [&_.table-simple-td]:tabular-nums ${density === 'compact' ? '[&_.table-simple-td]:!py-2 [&_.table-simple-td]:!text-xs' : '[&_.table-simple-td]:!py-3'}` : ''} ${className}`}>
            {/* Filters Bar Box (title, filterElement, filters, headerRight, filterFooter) */}
            {(title || (searchable && showSearch) || filterElement || filters || headerRight || filterFooter || filterPanel) && (
                <ListToolbar
                    title={title}
                    boxed={filterbox}
                    controls={filterElement || filters}
                    filterOpen={filterOpen}
                    onFilterToggle={onFilterToggle}
                    filterLabel={filterLabel}
                    trailing={toolbarTrailing}
                    footer={filterFooter}
                    panel={filterPanel}
                    search={
                        searchable && showSearch ?
                            <SearchInput
                                value={effectiveSearch}
                                onChange={handleSearchChange}
                                placeholder={resolvedSearchPlaceholder}
                            />
                        :
                            undefined
                    }
                />
            )}

            <Modal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                title={t('table.export.title')}
                maxWidth="sm"
            >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {(
                        [
                            { format: 'pdf', icon: 'pdf', color: 'red' },
                            { format: 'xlsx', icon: 'fileText', color: 'green' },
                            { format: 'ods', icon: 'table', color: 'green' },
                            { format: 'csv', icon: 'fileText', color: 'blue' },
                        ] as const
                    ).map(({ format, icon, color }) => (
                        <button
                            key={format}
                            type="button"
                            onClick={() => exportTable(format)}
                            disabled={exportingFormat !== null}
                            className={`group flex flex-col items-center justify-center gap-2 rounded-xl border px-3 py-4 text-xs font-semibold shadow-xs transition-all disabled:cursor-wait disabled:opacity-60 ${color === 'red' ? 'border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300 dark:hover:bg-red-950/40' : color === 'blue' ? 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-300 dark:hover:bg-blue-950/40' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-300 dark:hover:bg-emerald-950/40'}`}
                        >
                            <Icon
                                value={icon}
                                size={32}
                                className={`${color === 'red' ? 'text-red-600 dark:text-red-400' : color === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-emerald-600 dark:text-emerald-400'} transition-transform group-hover:scale-110`}
                            />
                            <div>{exportingFormat === format ? t('table.export.generating') : format.toUpperCase()}</div>
                        </button>
                    ))}
                </div>
            </Modal>

            {error && <Alert variant="error"><div className="flex flex-wrap items-center gap-2"><span>{error}</span>{onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>{t('workspace.list.retry')}</Button>}</div></Alert>}
            {/* Table Box */}
            <div className={`bg-white dark:bg-dark-box border border-slate-200/80 dark:border-white/16 overflow-hidden flex min-h-0 flex-col transition-all duration-300 ease-out ${isFullscreen ? 'fixed inset-0 z-[100] rounded-none border-0 shadow-2xl' : workspace ? 'rounded-lg' : 'rounded-xl shadow-xs'}`}>
                {/* Table wrapper */}
                <div
                    className={`scrollbar-primary overflow-auto relative flex-1 bg-white dark:bg-dark-box ${isFullscreen ? 'min-h-0' : workspace ? 'max-h-[calc(100dvh-16rem)]' : 'min-h-[300px]'}`}
                >
                {tableActions.length > 0 && rowActions.length === 0 && (
                    <div className="pointer-events-auto absolute right-2 top-1 z-20">
                        <TableHeaderActions actions={tableActions} position="right" />
                    </div>
                )}
                {isTableLoading && (
                    <div className={`absolute inset-0 z-10 flex items-center justify-center ${data.length > 0 ? 'bg-white/30 dark:bg-black/30' : 'bg-white/70 dark:bg-black/70 backdrop-blur-xs'}`}>
                        <div className="flex items-center space-x-2 text-slate-600 dark:text-zinc-200 font-semibold text-xs bg-white dark:bg-dark-box px-3 py-1.5 rounded-full shadow-sm border border-slate-200 dark:border-white/10">
                            <Icon value="loader" size={16} className="animate-spin text-primary" />
                            <span>{t('table.loading')}</span>
                        </div>
                    </div>
                )}

                <table className="table-simple" aria-busy={isTableLoading} inert={isTableLoading}>
                    <thead className={`table-simple-thead ${workspace ? 'sticky top-0 z-10' : ''}`}>
                        <tr className="table-simple-tr-head">
                            {actionsPosition === 'left' && rowActions.length > 0 && (
                                <th className="table-simple-th table-simple-th-actions">
                                    {tableActions.length > 0 ? (
                                        <TableHeaderActions actions={tableActions} position={actionsPosition} />
                                    ) : (
                                        resolvedActionsLabel
                                    )}
                                </th>
                            )}

                            {columns.map((col) => {
                                const actualSortKey = col.sortKey || col.key;
                                const isSorted = sortKey === actualSortKey;
                                return (
                                    <th
                                        key={col.key}
                                        aria-sort={col.sortable === false ? undefined : isSorted ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        className={`table-simple-th ${col.sortable !== false ? 'table-simple-th-sortable' : ''} ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || ''}`}
                                    >
                                        <button type="button" disabled={col.sortable === false} onClick={() => handleSort(actualSortKey)}
                                            className={`inline-flex w-full items-center gap-1.5 rounded outline-offset-4 focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-default ${col.align === 'center' ? 'justify-center' : col.align === 'right' ? 'justify-end' : 'text-left'}`}>
                                            <span>{col.label}</span>
                                            {col.sortable !== false && (
                                                <span className={`table-simple-th-sort-icon ${isSorted ? 'active' : 'inactive'}`}>
                                                    {isSorted ? (
                                                        <span className={`table-simple-sort-arrow ${sortOrder === 'desc' ? 'is-desc' : 'is-asc'}`}>
                                                            <Icon value="arrowUp" size={13} />
                                                        </span>
                                                    ) : (
                                                        <Icon value="arrowUpDown" size={12} />
                                                    )}
                                                </span>
                                            )}
                                        </button>
                                    </th>
                                );
                            })}

                            {actionsPosition === 'right' && rowActions.length > 0 && (
                                <th className="table-simple-th table-simple-th-actions">
                                    {tableActions.length > 0 ? (
                                        <TableHeaderActions actions={tableActions} position={actionsPosition} />
                                    ) : (
                                        resolvedActionsLabel
                                    )}
                                </th>
                            )}
                        </tr>
                    </thead>

                    <tbody className="table-simple-tbody">
                        {paginatedData.length === 0 ? (
                            <tr className={`table-simple-tr-empty ${isTableLoading ? "hidden" : ""}`}>
                                <td
                                    colSpan={columns.length + (rowActions.length > 0 ? 1 : 0)}
                                    className="table-simple-td-empty"
                                >
                                    <div className="sticky left-0 flex w-full max-w-[calc(100vw-3rem)] flex-col items-center justify-center space-y-2">
                                        <Icon value="inbox" size={32} className="text-slate-300 dark:text-zinc-600" />
                                        <span className="font-medium text-slate-600 dark:text-zinc-300" role="status">{isTableLoading ? t('table.loading') : error ? t('workspace.list.load_error') : resolvedEmptyText}</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((row, idx) => {
                                const isMenuOpen = false;

                                return (
                                    <tr
                                        key={String((row as Record<string, unknown>).id || idx)}
                                        onClick={() => onRowClick && onRowClick(row)}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        onKeyDown={event => { if(onRowClick && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')){ event.preventDefault(); onRowClick(row); } }}
                                        className={`table-simple-tr animate-in fade-in slide-in-from-top-1 duration-300 ${onRowClick ? 'table-simple-tr-clickable' : ''} ${isMenuOpen ? 'relative z-30' : ''} ${rowClassName ? rowClassName(row) : ''}`}
                                    >
                                        {/* Left Actions */}
                                        {actionsPosition === 'left' && rowActions.length > 0 && (
                                            <td className={`table-simple-td table-simple-td-actions ${isMenuOpen ? 'relative z-40' : ''}`} onClick={(e) => e.stopPropagation()}>
                                                {renderActionsContent(row)}
                                            </td>
                                        )}

                                        {/* Data Columns */}
                                        {columns.map((col) => {
                                            let cellContent: React.ReactNode = null;
                                            const value: unknown = Reflect.get(row, col.key);
                                            if(col.render){
                                                cellContent = col.render(row);
                                            }
                                            else{
                                                switch(col.type){
                                                    case 'multivalue': {
                                                        const items = col.multiValueExtractor
                                                            ? col.multiValueExtractor(row)
                                                            : (Array.isArray(value) ? (value as React.ReactNode[]) : []);

                                                        if(!items || items.length === 0){
                                                            cellContent = <span className="text-slate-400 text-sm">—</span>;
                                                        }
                                                        else{
                                                            const max = col.max ?? 2;
                                                            const shownItems = max > 0 ? items.slice(0, max) : [];
                                                            const hiddenCount = items.length - shownItems.length;
                                                            const hasMore = hiddenCount > 0;

                                                            cellContent = (
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    {shownItems.map((it, i) => (
                                                                        React.isValidElement(it) ? (
                                                                            <React.Fragment key={i}>{it}</React.Fragment>
                                                                        ) : (
                                                                            <Badge key={i} variant="primary">{it}</Badge>
                                                                        )
                                                                    ))}
                                                                    {hasMore && (
                                                                        <Tooltip title={t('common.showmore')}>
                                                                            <button
                                                                                type="button"
                                                                                className="h-7 px-2.5 rounded-full border border-slate-300 dark:border-zinc-700 flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 hover:text-primary hover:border-primary transition-all cursor-pointer shadow-xs ml-0.5"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setMultiValueSearch('');
                                                                                    setMultiValueModal({ isOpen: true, title: col.label, items });
                                                                                }}
                                                                            >
                                                                                <span>+{hiddenCount}</span>
                                                                            </button>
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                        break;
                                                    }

                                                    case 'date': {
                                                        const raw = value;
                                                        cellContent = raw ? date(raw as string | Date, false) : '—';
                                                        break;
                                                    }

                                                    case 'datetime': {
                                                        const raw = value;
                                                        cellContent = raw ? date(raw as string | Date, true) : '—';
                                                        break;
                                                    }

                                                    case 'time': {
                                                        const raw = String(value ?? '');
                                                        cellContent = raw ? raw.substring(0, 5) : '—';
                                                        break;
                                                    }

                                                    case 'price': {
                                                        const raw = value;
                                                        cellContent = raw !== null && raw !== undefined && raw !== '' ? price(raw) : '—';
                                                        break;
                                                    }

                                                    case 'percentage': {
                                                        const raw = value;
                                                        cellContent = raw !== null && raw !== undefined && raw !== '' ? percentage(raw) : '—';
                                                        break;
                                                    }

                                                    case 'boolean': {
                                                        const raw = value;
                                                        const isTrue = raw === true || raw === 1 || raw === '1' || raw === 'true';
                                                        const isFalse = raw === false || raw === 0 || raw === '0' || raw === 'false';
                                                        if(isTrue){
                                                            cellContent = <Badge variant="success">{t('common.yes')}</Badge>;
                                                        }
                                                        else{
                                                            if(isFalse){
                                                                cellContent = <Badge variant="danger">{t('common.no')}</Badge>;
                                                            }
                                                            else{
                                                                cellContent = '—';
                                                            }
                                                        }
                                                        break;
                                                    }

                                                    case 'number': {
                                                        const raw = value;
                                                        cellContent = typeof raw === 'number' ? raw.toLocaleString() : String(raw ?? '—');
                                                        break;
                                                    }

                                                    default:
                                                        cellContent = String(value ?? '');
                                                        break;
                                                }
                                            }

                                            return (
                                                <td
                                                    key={col.key}
                                                    className={`table-simple-td ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.nowrap ? 'whitespace-nowrap' : ''} ${col.className || ''}`}
                                                >
                                                    {cellContent}
                                                </td>
                                            );
                                        })}

                                        {/* Right Actions */}
                                        {actionsPosition === 'right' && rowActions.length > 0 && (
                                            <td className={`table-simple-td table-simple-td-actions ${isMenuOpen ? 'relative z-40' : ''}`} onClick={(e) => e.stopPropagation()}>
                                                {renderActionsContent(row)}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {paginated && <ListPagination page={currentPage} pageSize={pageSize} total={totalRecords} onPage={setCurrentPage} onPageSize={pagination?.onPageSize || (size => { setPageSize(size); setCurrentPage(1); })} pageSizeOptions={pageSizeOptions} />}
            </div>
            <Modal
                isOpen={multiValueModal.isOpen}
                onClose={() => setMultiValueModal(prev => ({ ...prev, isOpen: false }))}
                title={multiValueModal.title}
                cancelLabel={t('common.close')}
                maxWidth="md"
            >
                <div className="flex flex-col gap-4 pt-2 pb-2">
                    {multiValueModal.items.length > 5 && (
                        <SearchInput
                            value={multiValueSearch}
                            onChange={setMultiValueSearch}
                            placeholder={t('common.search')}
                        />
                    )}
                    <div className="flex flex-wrap justify-center gap-2.5 px-1 py-1">
                        {(() => {
                            const filtered = multiValueModal.items.filter(it => {
                                if(!multiValueSearch){
                                    return true;
                                }
                                const text = typeof it === 'string' || typeof it === 'number'
                                    ? String(it)
                                    : (React.isValidElement(it) && it.props && typeof (it.props as Record<string, unknown>).value === 'string'
                                        ? String((it.props as Record<string, unknown>).value)
                                        : '');
                                return text.toLowerCase().includes(multiValueSearch.toLowerCase());
                            });
                            if(filtered.length === 0){
                                return <div className="text-sm text-slate-500 w-full text-center py-4">{t('common.no_results')}</div>;
                            }
                            return filtered.map((it, i) => (
                                React.isValidElement(it) ? (
                                    <React.Fragment key={i}>{it}</React.Fragment>
                                ) : (
                                    <Badge key={i} variant="primary">{it}</Badge>
                                )
                            ));
                        })()}
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default TableSimple;
